/**
 * TestPressCard — compact SUMMARY of the Test Press workflow state
 * plus (Phase 2) the customer approve/reject affordance when the TP
 * is in ``arrived`` (= TP: Del.) state.
 *
 * Rendered in the OrderDetailPage sidebar for vinyl orders (or any
 * order that already carries a non-null test_press_status).
 *
 * Status + Expected Arrival remain driven by the parallel Test Press
 * supplier workflow (``SupplierConfirmationSection variant="test_press"``).
 * The ONLY mutation this card performs is the customer's approve /
 * reject decision via ``PATCH /api/orders/{id}/test-press-status``.
 * Reject requires a non-empty comment (enforced client AND server);
 * approve's comment is optional and sets the "OBS" indicator when
 * present.
 */
import { useState, useEffect } from 'react';
import { Disc3, CalendarClock, CheckCircle2, XCircle, AlertCircle, Loader2, Send, History, ChevronDown, ChevronRight, MessageSquare, ShieldAlert } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { TEST_PRESS_STATUS_LABELS, useTpStatusLabel } from './StatusBadge';
import { useTranslation } from '../contexts/TranslationContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { ordersApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_DOT = {
    'not_required':         'bg-slate-300',
    'required':             'bg-amber-400',
    'ordered':              'bg-blue-500',
    'confirmed':            'bg-sky-500',
    'dispatched':           'bg-indigo-500',
    'arrived':              'bg-amber-500',
    'approved':             'bg-emerald-500',
    'rejected':             'bg-red-500',
    'awaiting_feedback':    'bg-sky-500',
    // Legacy aliases
    'shipped':              'bg-indigo-500',
    'received_by_customer': 'bg-amber-500',
};


/**
 * PreviousTestPressesSection — collapsible list of sealed TP cycles
 * (V1, V2…). Fed by ``GET /orders/{id}/test-press/history`` which
 * returns admin-full snapshots and customer-sanitised snapshots
 * (Sony/LEOS/supplier fields stripped) on the server side.
 *
 * The newest active cycle lives on the parent TestPressCard — this
 * component renders only CLOSED versions beneath it. Section header
 * is a disclosure button; each version card expands to show the
 * cycle's final status, dates, customer comment/rejection reason,
 * and (admin only) Sony response note + supplier confirmation
 * dates. Nothing in here mutates the data — history is immutable.
 */
function PreviousTestPressesSection({ orderId, isAdmin, currentVersion, forceReloadKey }) {
    const [open, setOpen] = useState(false);
    const [versions, setVersions] = useState(null);  // null = not loaded
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});    // keyed by version number

    // Lazy-load on first open; refetch when the parent signals a new
    // cycle has been sealed (``forceReloadKey`` bumps).
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        ordersApi.getTestPressHistory(orderId)
            .then((r) => {
                if (cancelled) return;
                setVersions(r.data?.versions || []);
            })
            .catch(() => { if (!cancelled) setVersions([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, orderId, forceReloadKey]);

    // Nothing to disclose until there's at least one closed cycle.
    // Hide the entire section when the order is on V1 of its first
    // (and only) cycle to keep the common case uncluttered.
    if (!currentVersion || Number(currentVersion) <= 1) return null;

    const toggleVersion = (v) => setExpanded((e) => ({ ...e, [v]: !e[v] }));

    const renderVersion = (v) => {
        const tp = v.test_press_fields || {};
        const sc = v.supplier_confirmation || {};  // admin only
        const finalStatus = tp.test_press_status || 'rejected';
        // Admin-friendly label is the default; customer path must
        // mirror the main card's privacy rules — ``awaiting_feedback``
        // becomes "Processing" (never "Awaiting Sony"), and the
        // terminal states keep their standard labels.
        let label = TEST_PRESS_STATUS_LABELS[finalStatus] || finalStatus;
        if (!isAdmin && finalStatus === 'awaiting_feedback') {
            label = 'Processing';
        }
        const dot = STATUS_DOT[finalStatus] || 'bg-slate-300';
        const comment = tp.test_press_customer_comment;
        const decidedAt = tp.test_press_customer_decision_at;
        const isOpen = !!expanded[v.version];
        return (
            <div
                key={v.version}
                className="border border-slate-200 rounded-md overflow-hidden"
                data-testid={`tp-history-v${v.version}`}
            >
                <button
                    type="button"
                    onClick={() => toggleVersion(v.version)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                    data-testid={`tp-history-v${v.version}-toggle`}
                >
                    {isOpen
                        ? <ChevronDown className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                        : <ChevronRight className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                    <span className="text-sm font-semibold text-slate-800">V{v.version}</span>
                    <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
                    <span className="text-xs text-slate-600">{label}</span>
                    {v.closed_at && (
                        <span className="text-[10px] text-slate-400 ml-auto">
                            Closed {formatDate(v.closed_at)}
                        </span>
                    )}
                </button>
                {isOpen && (
                    <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-2" data-testid={`tp-history-v${v.version}-body`}>
                        {/* Customer decision + comment / rejection
                            reason — single most useful line, shown
                            to both admin and customer. */}
                        {decidedAt && (
                            <p className="text-[10px] text-slate-500">
                                Customer {finalStatus === 'rejected' ? 'rejected' : finalStatus === 'approved' ? 'approved' : 'decided'}
                                {' '}on {formatDate(decidedAt)}
                                {tp.test_press_customer_decision_by_name
                                    ? ` · ${tp.test_press_customer_decision_by_name}`
                                    : ''}
                            </p>
                        )}
                        {comment && (
                            <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1 mb-0.5">
                                    <MessageSquare className="h-3 w-3" />
                                    {finalStatus === 'rejected' ? 'Rejection reason' : 'Customer comment'}
                                </div>
                                <p className="text-xs text-slate-700 whitespace-pre-wrap leading-snug">{comment}</p>
                            </div>
                        )}
                        {/* Relevant dates — expected arrival of the
                            cycle (always), and the dispatch planned
                            arrival (admin only, if present). */}
                        {tp.test_press_expected_arrival && (
                            <p className="text-[11px] text-slate-600">
                                <span className="text-slate-400">Expected arrival:</span>{' '}
                                {formatDate(tp.test_press_expected_arrival)}
                            </p>
                        )}
                        {isAdmin && sc.dispatch_planned_arrival_date && (
                            <p className="text-[11px] text-slate-600">
                                <span className="text-slate-400">Dispatch planned:</span>{' '}
                                {formatDate(sc.dispatch_planned_arrival_date)}
                            </p>
                        )}
                        {/* Admin-only Sony / supplier context for the
                            cycle. Scrubbed from the customer payload
                            on the server. */}
                        {isAdmin && (sc.sony_leos_number || sc.po_sent_at || tp.test_press_sony_feedback_text || tp.test_press_sony_response_branch) && (
                            <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1">
                                <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
                                    Internal (Sony / supplier)
                                </div>
                                {sc.sony_leos_number && (
                                    <p className="text-[11px] text-slate-700">LEOS: {sc.sony_leos_number}</p>
                                )}
                                {sc.po_sent_at && (
                                    <p className="text-[11px] text-slate-700">PO sent: {formatDate(sc.po_sent_at)}</p>
                                )}
                                {tp.test_press_sony_feedback_text && (
                                    <p className="text-[11px] text-slate-700 whitespace-pre-wrap">
                                        <span className="text-slate-500">Feedback to Sony:</span>{' '}
                                        {tp.test_press_sony_feedback_text}
                                    </p>
                                )}
                                {v.sony_response_note && (
                                    <p className="text-[11px] text-slate-700 whitespace-pre-wrap">
                                        <span className="text-slate-500">Sony response note:</span>{' '}
                                        {v.sony_response_note}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="mt-3 pt-3 border-t border-slate-200" data-testid="tp-history-section">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 text-left text-[11px] uppercase tracking-wide font-semibold text-slate-500 hover:text-slate-700"
                data-testid="tp-history-toggle"
            >
                {open
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronRight className="h-3.5 w-3.5" />}
                <History className="h-3.5 w-3.5" />
                Previous test presses
                <span className="ml-1 text-slate-400 normal-case font-normal">
                    ({Math.max(0, Number(currentVersion) - 1)})
                </span>
            </button>
            {open && (
                <div className="mt-2 space-y-2">
                    {loading && (
                        <p className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading history…
                        </p>
                    )}
                    {!loading && versions && versions.length === 0 && (
                        <p className="text-xs text-slate-500">No previous test press cycles.</p>
                    )}
                    {!loading && versions && versions.map(renderVersion)}
                </div>
            )}
        </div>
    );
}

export function TestPressCard({ order, onUpdated }) {
    const { isAdmin } = useAuth();
    const { t } = useTranslation();
    const tpLabel = useTpStatusLabel();
    const current = order?.test_press_status || 'not_required';
    const arrivalRelevant = current !== 'not_required';
    // Customer-visible distinction: once admin applies a Phase 4
    // "New Test Press (V2)" response, the TP opens a completely
    // new supplier cycle starting at REQUIRED (admin still has
    // to send the V2 PO to Sony). To the customer this needs to
    // read differently from the first cycle — their feedback has
    // been actioned and a new TP is being arranged. We piggy-back
    // on persisted ``test_press_sony_response_branch`` ("new_tp_v2")
    // + a version counter > 1 so the override survives refresh
    // and stays correct across the full V2 cycle until it leaves
    // the pre-arrival states.
    const tpVersion = Number(order?.test_press_version || 1);
    const isV2CycleActive = (
        tpVersion > 1
        && (order?.test_press_sony_response_branch === 'new_tp_v2')
        && (current === 'required' || current === 'ordered'
            || current === 'confirmed' || current === 'dispatched')
    );

    // Phase 2 — approve/reject state.
    const [dialog, setDialog] = useState(null);  // 'approve' | 'reject' | null
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Phase 3 — admin "Send feedback to Sony" dialog state. Pre-filled
    // from the customer's rejection comment but the admin can edit
    // before submit. Only reachable from ``rejected`` by an admin.
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [sendingFeedback, setSendingFeedback] = useState(false);
    // Compact recipient preview — resolved server-side with the exact
    // same routing logic the real send uses. Loaded when the dialog
    // opens; null while loading/unresolved. Send stays disabled until
    // this comes back with ``can_send: true``.
    const [recipientPreview, setRecipientPreview] = useState(null);
    const [loadingRecipientPreview, setLoadingRecipientPreview] = useState(false);

    // Phase 4 — admin "Sony response" dialog. One of three branches;
    // branch is chosen upfront, note is optional and admin-only.
    const [sonyResponseBranch, setSonyResponseBranch] = useState(null);
    const [sonyResponseNote, setSonyResponseNote] = useState('');
    const [applyingSonyResponse, setApplyingSonyResponse] = useState(false);

    // Admin-only "Override Test Press status" dialog state. Legacy
    // production orders (status set before the state machine existed)
    // sometimes read e.g. "Dispatched" when the real-world cycle was
    // already Approved. The override routes through the state-machine
    // service with ``force=True`` + ``MANUAL_ADMIN`` event, records a
    // reason onto the audit row, and never sends customer/Sony mails
    // or touches the production supplier confirmation. See
    // ``POST /orders/{id}/test-press/admin-override``.
    const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
    const [overrideStatus, setOverrideStatus] = useState('');
    const [overrideReason, setOverrideReason] = useState('');
    const [applyingOverride, setApplyingOverride] = useState(false);

    // The customer can decide when TP has reached "arrived" (= Del.).
    // Admins can also use these buttons as a shortcut; the backend
    // routes both actors through the same state-machine events.
    const canDecide = current === 'arrived';
    const canSendFeedbackToSony = isAdmin && current === 'rejected';
    const feedbackSentAlready = current === 'awaiting_feedback';
    // Phase 4 — admin can pick a Sony response branch ONLY while the
    // TP is still awaiting feedback. Once a branch is applied, the
    // state leaves ``awaiting_feedback`` and the three buttons
    // naturally become unreachable until the next cycle. The
    // persisted ``test_press_sony_response_branch`` is used by the
    // "Branch applied" readout further down (admin-only).
    const canApplySonyResponse = isAdmin && current === 'awaiting_feedback';
    const branchApplied = order?.test_press_sony_response_branch || null;

    // Customer comment (approve-with-comment OR rejection reason).
    const customerComment = (order?.test_press_customer_comment || '').trim();
    // OBS is an admin-only review signal. Customers don't need an
    // "attention" chip about their own approval comment, especially
    // once the TP is Approved — that's a completed positive state.
    const hasObs = isAdmin && !!order?.test_press_customer_comment_has_obs;

    const formatArrival = (iso) => {
        if (!iso) return '';
        const [y, m, d] = iso.split('-');
        try {
            return new Date(Date.UTC(+y, +m - 1, +d)).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
            });
        } catch (_e) {
            return iso;
        }
    };

    const openApprove = () => { setComment(''); setDialog('approve'); };
    const openReject = () => { setComment(''); setDialog('reject'); };
    const close = () => { setDialog(null); setComment(''); };

    const openSendFeedback = () => {
        // Pre-fill with the customer's reject comment so the admin
        // can forward verbatim or edit before sending.
        setFeedbackText(customerComment || '');
        setFeedbackDialogOpen(true);
        setRecipientPreview(null);
        setLoadingRecipientPreview(true);
        ordersApi.previewTestPressFeedbackToSony(order.id)
            .then((resp) => setRecipientPreview(resp?.data || resp))
            .catch((err) => {
                setRecipientPreview({
                    can_send: false,
                    reason: err?.response?.data?.detail || 'Could not resolve the recipient — please retry.',
                    partner_name: null, to: null, cc: [], reply_to: null,
                });
            })
            .finally(() => setLoadingRecipientPreview(false));
    };
    const closeFeedback = () => {
        setFeedbackDialogOpen(false);
        setFeedbackText('');
        setRecipientPreview(null);
    };

    const submitFeedbackToSony = async () => {
        const trimmed = feedbackText.trim();
        if (!trimmed) {
            toast.error('Please add the feedback text you want to send to Sony.');
            return;
        }
        setSendingFeedback(true);
        try {
            const updated = await ordersApi.sendTestPressFeedbackToSony(order.id, trimmed);
            toast.success('Feedback sent to Sony — the customer has been notified.');
            closeFeedback();
            if (onUpdated) onUpdated(updated?.data || updated);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not send the feedback — please try again.';
            toast.error(msg);
        } finally {
            setSendingFeedback(false);
        }
    };

    const PHASE4_BRANCH_LABELS = {
        // Button + dialog title. Deliberately NO version number here —
        // the admin button label stays stable across cycles ("New TP"
        // always means "open a new TP cycle"), while the actual next
        // version (V2 → V3 → V4…) is computed server-side from the
        // live counter when the branch is applied. See
        // ``_open_new_tp_cycle`` in ``routes/orders.py``.
        new_tp_v2:    { title: 'New Test Press',            desc: 'Sony will send another Test Press. Current cycle resets so the customer can review the new TP.' },
        fix_in_plant: { title: 'Fix without new TP',        desc: 'Sony fixes in-plant without a new TP. TP is marked Approved so production can proceed.' },
        new_master:   { title: 'New master required',       desc: 'Customer must upload a new audio master. TP goes back to Ordered and the customer is emailed.' },
    };
    const openSonyResponse = (branch) => {
        setSonyResponseBranch(branch);
        setSonyResponseNote('');
    };
    const closeSonyResponse = () => { setSonyResponseBranch(null); setSonyResponseNote(''); };
    const submitSonyResponse = async () => {
        if (!sonyResponseBranch) return;
        setApplyingSonyResponse(true);
        try {
            const resp = await ordersApi.applyTestPressSonyResponse(
                order.id,
                sonyResponseBranch,
                sonyResponseNote.trim() || null,
            );
            const label = PHASE4_BRANCH_LABELS[sonyResponseBranch]?.title || sonyResponseBranch;
            toast.success(`Applied: ${label}`);
            closeSonyResponse();
            if (onUpdated) onUpdated(resp?.data || resp);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not apply this response — please try again.';
            toast.error(msg);
        } finally {
            setApplyingSonyResponse(false);
        }
    };

    // ── Admin-only override — legacy fix-up path ──
    const OVERRIDE_STATUS_OPTIONS = [
        // Order mirrors the TP_PROGRESSION ladder so the admin sees
        // the state machine in-order. ``not_required`` is intentionally
        // excluded — the server rejects it (disabling has its own
        // semantics outside this fix-up flow).
        'required',
        'ordered',
        'confirmed',
        'dispatched',
        'arrived',
        'approved',
        'rejected',
        'awaiting_feedback',
    ];
    const openOverride = () => {
        setOverrideStatus(current && current !== 'not_required' ? current : '');
        setOverrideReason('');
        setOverrideDialogOpen(true);
    };
    const closeOverride = () => {
        setOverrideDialogOpen(false);
        setOverrideStatus('');
        setOverrideReason('');
    };
    const submitOverride = async () => {
        const trimmed = overrideReason.trim();
        if (!overrideStatus) {
            toast.error('Please pick the Test Press status to apply.');
            return;
        }
        if (!trimmed) {
            toast.error('Please add a short reason — it is recorded in the audit log.');
            return;
        }
        setApplyingOverride(true);
        try {
            const resp = await ordersApi.adminOverrideTestPressStatus(
                order.id,
                overrideStatus,
                trimmed,
            );
            toast.success(
                `Test Press status set to "${TEST_PRESS_STATUS_LABELS[overrideStatus] || overrideStatus}" — override recorded.`
            );
            closeOverride();
            if (onUpdated) onUpdated(resp?.data || resp);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not apply the override — please try again.';
            toast.error(msg);
        } finally {
            setApplyingOverride(false);
        }
    };

    const submit = async () => {
        if (!dialog) return;
        const trimmed = comment.trim();
        if (dialog === 'reject' && !trimmed) {
            // Enforced on the server too (422), but surface a friendly
            // inline error without a round-trip.
            toast.error('Please add a comment explaining why you\'re rejecting the Test Press.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                status: dialog === 'approve' ? 'approved' : 'rejected',
                customer_comment: trimmed || null,
            };
            const updated = await ordersApi.updateTestPressStatus(order.id, payload);
            toast.success(
                dialog === 'approve'
                    ? 'Thanks — your Test Press has been approved.'
                    : 'Your Test Press rejection has been recorded.'
            );
            close();
            if (onUpdated) onUpdated(updated?.data || updated);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not save your decision — please try again.';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className={`bg-white rounded-lg p-3 border shadow-premium-sm ${
                canDecide ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-300'
            }`}
            data-testid="test-press-card"
        >
            <div className="flex items-center gap-1.5 text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-2">
                <Disc3 className="h-3 w-3" />
                {t('tp.card.title', 'Test Press')}
            </div>

            {canDecide && (
                <div
                    className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 flex items-start gap-2"
                    data-testid="test-press-action-required"
                >
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white font-bold text-xs flex-shrink-0">!</span>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-900">
                            {t('tp.action_required_title', 'Action required · Test Press: Review')}
                        </p>
                        <p className="text-xs text-amber-800 mt-0.5 leading-snug">
                            {t('tp.action_required_desc', 'Your test press has been delivered. Please approve or reject it below so we can continue production.')}
                        </p>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT[current] || 'bg-slate-300'}`} />
                <span className="text-sm text-slate-800 font-medium" data-testid="test-press-current-label">
                    {current === 'arrived'
                        ? t('tp.state.customer_review', 'Test Press: Review')
                        : (current === 'awaiting_feedback' && !isAdmin)
                            ? t('tp.state.customer_processing', 'Test Press: Processing')
                            : (isV2CycleActive && !isAdmin)
                                ? t('tp.state.customer_new_in_production', 'Test Press: New test press in production')
                                : tpLabel(current)}
                </span>
                {hasObs && (
                    <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                        title={t('tp.obs_tooltip', 'Customer approved the Test Press and left a comment — please review.')}
                        data-testid="test-press-obs"
                    >
                        <AlertCircle className="h-3 w-3" /> OBS
                    </span>
                )}
            </div>

            {/* Customer reassurance after admin applied the Phase 4
                "New Test Press" response. Internal / supplier wording
                is deliberately absent per the privacy rule. Version
                number is dynamic — V2, V3, V4… — reading the live
                counter, which is bumped from the highest existing
                cycle each time a new rejection → new TP is applied. */}
            {isV2CycleActive && !isAdmin && (
                <p
                    className="text-xs text-slate-600 leading-snug mt-1"
                    data-testid="test-press-v2-customer-notice"
                >
                    {t('tp.v2_customer_notice', 'Your feedback has been reviewed and a new Test Press (V{n}) is being produced.', { n: tpVersion })}
                </p>
            )}

            {arrivalRelevant && !canDecide && order?.test_press_expected_arrival && current !== 'approved' && current !== 'rejected' && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase text-slate-500 font-semibold tracking-wide">
                        <CalendarClock className="h-3 w-3" />
                        {t('tp.expected_arrival', 'Expected Arrival')}
                    </div>
                    <p className="text-sm text-slate-800 font-medium mt-1"
                       data-testid="test-press-expected-arrival-value">
                        {formatArrival(order.test_press_expected_arrival)}
                    </p>
                </div>
            )}

            {canDecide && (
                <div className="mt-3 pt-3 border-t border-slate-200 flex gap-2">
                    <Button
                        type="button"
                        size="sm"
                        onClick={openApprove}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="test-press-approve-btn"
                    >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> {t('common.approve', 'Approve')}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={openReject}
                        className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
                        data-testid="test-press-reject-btn"
                    >
                        <XCircle className="h-4 w-4 mr-1" /> {t('common.reject', 'Reject')}
                    </Button>
                </div>
            )}

            {customerComment && (current === 'approved' || current === 'rejected' || current === 'awaiting_feedback') && (
                <div
                    className={`mt-3 pt-3 border-t border-slate-200 ${current === 'approved' ? 'text-slate-700' : current === 'awaiting_feedback' ? 'text-slate-700' : 'text-red-700'}`}
                    data-testid="test-press-customer-comment"
                >
                    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1">
                        {current === 'approved' ? 'Customer comment' : 'Rejection reason'}
                    </div>
                    <p className="text-xs leading-snug whitespace-pre-wrap">{customerComment}</p>
                    {order?.test_press_customer_decision_at && (
                        <p className="text-[10px] text-slate-400 mt-1">
                            {formatDate(order.test_press_customer_decision_at)}
                            {order.test_press_customer_decision_by_name
                                ? ` · ${order.test_press_customer_decision_by_name}`
                                : ''}
                        </p>
                    )}
                </div>
            )}

            {canSendFeedbackToSony && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                    <Button
                        type="button"
                        size="sm"
                        onClick={openSendFeedback}
                        className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                        data-testid="test-press-send-feedback-btn"
                    >
                        <Send className="h-4 w-4 mr-1" /> Send feedback to Sony
                    </Button>
                    <p className="text-[10px] text-slate-400 mt-1.5 italic">
                        Forward the rejection to Sony to request the next step.
                    </p>
                </div>
            )}

            {feedbackSentAlready && !isAdmin && (
                <div
                    className="mt-3 pt-3 border-t border-slate-200"
                    data-testid="test-press-processing-notice"
                >
                    <p className="text-xs text-slate-700 leading-snug">
                        Your feedback has been received. We are arranging the next step and will update you when there is news.
                    </p>
                </div>
            )}

            {feedbackSentAlready && isAdmin && (
                <div
                    className="mt-3 pt-3 border-t border-slate-200"
                    data-testid="test-press-feedback-sent"
                >
                    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1 flex items-center gap-1">
                        <Send className="h-3 w-3" />
                        Feedback sent to Sony
                    </div>
                    {order?.test_press_sony_feedback_text && (
                        <p className="text-xs text-slate-700 leading-snug whitespace-pre-wrap">
                            {order.test_press_sony_feedback_text}
                        </p>
                    )}
                    {order?.test_press_sony_feedback_sent_at && (
                        <p className="text-[10px] text-slate-400 mt-1">
                            {formatDate(order.test_press_sony_feedback_sent_at)}
                            {order.test_press_sony_feedback_sent_by_name
                                ? ` · ${order.test_press_sony_feedback_sent_by_name}`
                                : ''}
                            {order.test_press_sony_feedback_recipient
                                ? ` · to ${order.test_press_sony_feedback_recipient}`
                                : ''}
                        </p>
                    )}

                    {canApplySonyResponse && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                            <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-2">
                                Apply Sony response
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <Button
                                    type="button" size="sm" variant="outline"
                                    onClick={() => openSonyResponse('new_tp_v2')}
                                    className="h-auto whitespace-normal py-2 text-xs"
                                    data-testid="tp-sony-branch-new_tp_v2"
                                >
                                    New TP
                                </Button>
                                <Button
                                    type="button" size="sm" variant="outline"
                                    onClick={() => openSonyResponse('fix_in_plant')}
                                    className="h-auto whitespace-normal py-2 text-xs"
                                    data-testid="tp-sony-branch-fix_in_plant"
                                >
                                    Fix without new TP
                                </Button>
                                <Button
                                    type="button" size="sm" variant="outline"
                                    onClick={() => openSonyResponse('new_master')}
                                    className="h-auto whitespace-normal py-2 text-xs"
                                    data-testid="tp-sony-branch-new_master"
                                >
                                    New master required
                                </Button>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1.5 italic">
                                Pick the path Sony has advised. The choice is locked for this TP cycle.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {isAdmin && branchApplied && current !== 'awaiting_feedback' && current !== 'rejected' && (
                <div
                    className="mt-3 pt-3 border-t border-slate-200"
                    data-testid="test-press-branch-applied"
                >
                    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1 flex items-center gap-1">
                        <Send className="h-3 w-3" />
                        Sony response applied
                    </div>
                    <p className="text-xs text-slate-700 leading-snug">
                        <span className="font-semibold">{PHASE4_BRANCH_LABELS[branchApplied]?.title || branchApplied}</span>
                        {order?.test_press_version && branchApplied === 'new_tp_v2' && (
                            <> · <span className="text-slate-500">V{order.test_press_version}</span></>
                        )}
                    </p>
                    {order?.test_press_sony_response_note && (
                        <p className="text-xs text-slate-700 leading-snug whitespace-pre-wrap mt-1">
                            {order.test_press_sony_response_note}
                        </p>
                    )}
                    {order?.test_press_sony_response_applied_at && (
                        <p className="text-[10px] text-slate-400 mt-1">
                            {formatDate(order.test_press_sony_response_applied_at)}
                            {order.test_press_sony_response_applied_by_name
                                ? ` · ${order.test_press_sony_response_applied_by_name}`
                                : ''}
                        </p>
                    )}
                </div>
            )}

            {order?.test_press_updated_at && current !== 'not_required' && !customerComment && (
                <p className="text-[10px] text-slate-400 mt-2">
                    Updated {formatDate(order.test_press_updated_at)}
                    {order.test_press_updated_by_name ? ` · ${order.test_press_updated_by_name}` : ''}
                </p>
            )}

            {isAdmin && (
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                    <p className="text-[10px] text-slate-400 italic">
                        Managed via <span className="font-semibold">Supplier → Test Press</span>
                    </p>
                    <button
                        type="button"
                        onClick={openOverride}
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 hover:text-slate-900 hover:underline"
                        data-testid="test-press-admin-override-btn"
                        title="Correct the Test Press status on legacy orders. Does not send emails or touch the production PO."
                    >
                        <ShieldAlert className="h-3 w-3" />
                        Override status
                    </button>
                </div>
            )}

            {/* Immutable history of closed TP cycles. Rendered only
                when a V2+ exists — fetched lazily on first expand.
                Admin sees full snapshot (Sony/supplier context);
                customer sees the sanitised payload. */}
            <PreviousTestPressesSection
                orderId={order?.id}
                isAdmin={isAdmin}
                currentVersion={order?.test_press_version}
                forceReloadKey={order?.test_press_updated_at}
            />

            <AlertDialog open={!!dialog} onOpenChange={(open) => { if (!open) close(); }}>
                <AlertDialogContent data-testid="test-press-decision-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {dialog === 'approve'
                                ? 'Approve this Test Press?'
                                : 'Reject this Test Press?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <p>
                                    {dialog === 'approve'
                                        ? 'Once approved, production of your final order can proceed. You can optionally add a note — anything you flag here is passed on to the pressing plant.'
                                        : 'Please tell us what went wrong — your comment is required and will be shared with the pressing plant.'}
                                </p>
                                <div className="space-y-1">
                                    <Label htmlFor="tp-comment" className="text-xs">
                                        {dialog === 'approve'
                                            ? 'Comment (optional)'
                                            : <span>Reason for rejection <span className="text-red-600">*</span></span>}
                                    </Label>
                                    <Textarea
                                        id="tp-comment"
                                        rows={4}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder={dialog === 'approve'
                                            ? 'e.g. "Please double-check the dead-wax matrix."'
                                            : 'e.g. "Audible pop at 2:17 on side B track 2."'}
                                        className="text-sm"
                                        data-testid="test-press-decision-comment"
                                    />
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={submitting} data-testid="test-press-decision-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            disabled={submitting || (dialog === 'reject' && !comment.trim())}
                            onClick={(e) => { e.preventDefault(); submit(); }}
                            className={dialog === 'approve'
                                ? 'bg-emerald-600 hover:bg-emerald-700'
                                : 'bg-red-600 hover:bg-red-700'}
                            data-testid="test-press-decision-confirm"
                        >
                            {submitting
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : (dialog === 'approve'
                                    ? <CheckCircle2 className="h-4 w-4 mr-2" />
                                    : <XCircle className="h-4 w-4 mr-2" />)}
                            {dialog === 'approve' ? 'Approve Test Press' : 'Submit rejection'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={feedbackDialogOpen} onOpenChange={(open) => { if (!open) closeFeedback(); }}>
                <AlertDialogContent data-testid="test-press-feedback-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Send feedback to Sony</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <p>
                                    Review and edit the customer's rejection comment before forwarding it to Sony.
                                    Sony will reply with the next step (new Test Press, in-plant fix, or new master required).
                                </p>
                                <div className="space-y-1">
                                    <Label htmlFor="tp-sony-feedback" className="text-xs">
                                        Feedback to Sony <span className="text-red-600">*</span>
                                    </Label>
                                    <Textarea
                                        id="tp-sony-feedback"
                                        rows={6}
                                        value={feedbackText}
                                        onChange={(e) => setFeedbackText(e.target.value)}
                                        placeholder="e.g. Customer flagged an audible pop at 2:17 on side B track 2 — please advise if a new TP is needed."
                                        className="text-sm"
                                        data-testid="test-press-feedback-text"
                                    />
                                    <p className="text-[10px] text-slate-400">
                                        The customer will be notified automatically that their feedback has been forwarded.
                                    </p>
                                </div>

                                {/* Compact recipient preview — resolved with the exact
                                    same routing logic as the real send. Never a guess:
                                    loading / blocked / resolved are the only 3 states. */}
                                <div
                                    className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs space-y-1"
                                    data-testid="tp-sony-recipient-preview"
                                >
                                    {loadingRecipientPreview && (
                                        <div className="flex items-center gap-1.5 text-slate-500" data-testid="tp-sony-recipient-preview-loading">
                                            <Loader2 className="h-3 w-3 animate-spin" /> Resolving recipient…
                                        </div>
                                    )}
                                    {!loadingRecipientPreview && recipientPreview?.can_send && (
                                        <>
                                            <div className="font-medium text-slate-600">
                                                Will be sent via Partner: <span className="text-slate-900">{recipientPreview.partner_name}</span>
                                            </div>
                                            <div className="grid grid-cols-[3.5rem_1fr] gap-x-2 text-slate-600">
                                                <span className="text-slate-400">To:</span>
                                                <span className="text-slate-900" data-testid="tp-sony-recipient-to">{recipientPreview.to}</span>
                                                {recipientPreview.cc.length > 0 && (
                                                    <>
                                                        <span className="text-slate-400">CC:</span>
                                                        <span className="text-slate-900" data-testid="tp-sony-recipient-cc">{recipientPreview.cc.join(', ')}</span>
                                                    </>
                                                )}
                                                <span className="text-slate-400">Reply-To:</span>
                                                <span className="text-slate-900" data-testid="tp-sony-recipient-reply-to">
                                                    {recipientPreview.reply_to || '—'}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {!loadingRecipientPreview && recipientPreview && !recipientPreview.can_send && (
                                        <div className="flex items-start gap-1.5 text-red-700" data-testid="tp-sony-recipient-blocked">
                                            <ShieldAlert className="h-3.5 w-3.5 mt-px shrink-0" />
                                            <span>{recipientPreview.reason}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={sendingFeedback} data-testid="test-press-feedback-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            disabled={sendingFeedback || !feedbackText.trim() || loadingRecipientPreview || !recipientPreview?.can_send}
                            onClick={(e) => { e.preventDefault(); submitFeedbackToSony(); }}
                            className="bg-sky-600 hover:bg-sky-700"
                            data-testid="test-press-feedback-confirm"
                        >
                            {sendingFeedback
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <Send className="h-4 w-4 mr-2" />}
                            Send to Sony
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!sonyResponseBranch}
                onOpenChange={(open) => { if (!open) closeSonyResponse(); }}
            >
                <AlertDialogContent data-testid="tp-sony-response-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Apply Sony response: {PHASE4_BRANCH_LABELS[sonyResponseBranch]?.title || ''}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <p className="text-slate-700">
                                    {PHASE4_BRANCH_LABELS[sonyResponseBranch]?.desc}
                                </p>
                                {sonyResponseBranch === 'new_master' && (
                                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                        The customer will receive a neutral email asking them to upload a new master.
                                    </p>
                                )}
                                <div className="space-y-1">
                                    <Label htmlFor="tp-sony-response-note" className="text-xs">
                                        Internal note (optional, admin-only)
                                    </Label>
                                    <Textarea
                                        id="tp-sony-response-note"
                                        rows={3}
                                        value={sonyResponseNote}
                                        onChange={(e) => setSonyResponseNote(e.target.value)}
                                        placeholder="e.g. Sony confirmed V2 will ship within 7 days"
                                        className="text-sm"
                                        data-testid="tp-sony-response-note"
                                    />
                                </div>
                                <p className="text-[11px] text-slate-500 italic">
                                    This choice is locked for the current Test Press cycle.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={applyingSonyResponse}
                            data-testid="tp-sony-response-cancel"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            disabled={applyingSonyResponse}
                            onClick={(e) => { e.preventDefault(); submitSonyResponse(); }}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid="tp-sony-response-confirm"
                        >
                            {applyingSonyResponse
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <Send className="h-4 w-4 mr-2" />}
                            Apply
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <AlertDialog
                open={overrideDialogOpen}
                onOpenChange={(open) => { if (!open) closeOverride(); }}
            >
                <AlertDialogContent data-testid="test-press-override-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-amber-600" />
                            Override Test Press status
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <p className="text-slate-700">
                                    Use this to correct the Test Press status on legacy orders where the
                                    real-world cycle completed before the current workflow existed. The
                                    override is recorded in the audit log and will <span className="font-semibold">not</span> send
                                    any customer or Sony emails, and will <span className="font-semibold">not</span> change the
                                    Production Order supplier data.
                                </p>
                                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                                    <span className="font-semibold">Current status:</span>{' '}
                                    {TEST_PRESS_STATUS_LABELS[current] || current}
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="tp-override-status" className="text-xs">
                                        New status <span className="text-red-600">*</span>
                                    </Label>
                                    <Select value={overrideStatus} onValueChange={setOverrideStatus}>
                                        <SelectTrigger
                                            id="tp-override-status"
                                            className="text-sm"
                                            data-testid="test-press-override-status"
                                        >
                                            <SelectValue placeholder="Pick a Test Press status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {OVERRIDE_STATUS_OPTIONS.map((s) => (
                                                <SelectItem
                                                    key={s}
                                                    value={s}
                                                    data-testid={`test-press-override-status-${s}`}
                                                >
                                                    {TEST_PRESS_STATUS_LABELS[s] || s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="tp-override-reason" className="text-xs">
                                        Reason <span className="text-red-600">*</span>
                                    </Label>
                                    <Textarea
                                        id="tp-override-reason"
                                        rows={3}
                                        value={overrideReason}
                                        onChange={(e) => setOverrideReason(e.target.value)}
                                        placeholder='e.g. "Legacy order — TP was approved by customer back in 2024, pre-workflow."'
                                        className="text-sm"
                                        data-testid="test-press-override-reason"
                                    />
                                    <p className="text-[10px] text-slate-400">
                                        Written to the Test Press audit log and tagged as a manual admin override.
                                    </p>
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={applyingOverride}
                            data-testid="test-press-override-cancel"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            disabled={applyingOverride || !overrideStatus || !overrideReason.trim()}
                            onClick={(e) => { e.preventDefault(); submitOverride(); }}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="test-press-override-confirm"
                        >
                            {applyingOverride
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <ShieldAlert className="h-4 w-4 mr-2" />}
                            Apply override
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default TestPressCard;
